import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { isTopLevelRoute } from './parentRegistry';

export type BackBarOverride = {
  to: string;
  label: string;
};

export type TrailEntry = {
  /** Full path (pathname + search). */
  to: string;
  /** Display name the page registered for itself (e.g. project name), if any. */
  label: string | null;
};

type BackBarContextValue = {
  override: BackBarOverride | null;
  setOverride: (next: BackBarOverride | null) => void;
  setPageLabel: (label: string | null) => void;
  /** The in-app page the user navigated here from, if known. */
  from: TrailEntry | null;
};

const BackBarContext = createContext<BackBarContextValue | null>(null);

type TrailState = {
  key: string;
  path: string;
  stack: TrailEntry[];
};

export function BackBarProvider({ children }: { children: React.ReactNode }) {
  const [override, setOverrideState] = useState<BackBarOverride | null>(null);

  const setOverride = useCallback((next: BackBarOverride | null) => {
    setOverrideState(next);
  }, []);

  // Display name the *current* page registered for itself. Held in a ref so
  // updating it doesn't re-render; it's only read at navigation time, when it
  // still describes the page being left.
  const pageLabelRef = useRef<string | null>(null);
  const setPageLabel = useCallback((label: string | null) => {
    pageLabelRef.current = label;
  }, []);

  // In-memory breadcrumb trail of visited in-app locations. Memory-only on
  // purpose: after a hard refresh or deep link the trail is empty and the
  // BackBar falls back to the hierarchical parent registry.
  //
  // Breadcrumb semantics: A → B → C means the bar on C points to B; going
  // back to B (by any means) unwinds the trail so B points to A — never to C.
  //
  // The trail is updated synchronously during render (not in an effect) so the
  // very first paint of a new page already sees the correct origin — no flash
  // of a stale or fallback target.
  const location = useLocation();
  const navigationType = useNavigationType();
  const currentPath = location.pathname + location.search;
  const [trail, setTrail] = useState<TrailState>(() => ({
    key: location.key,
    path: currentPath,
    stack: [],
  }));

  if (trail.key !== location.key) {
    const prev = trail.path;
    const viaBackBar = (location.state as { backBarNav?: boolean } | null)?.backBarNav === true;

    let stack = trail.stack;
    if (prev === currentPath) {
      // Same page, new history entry (e.g. query-only churn handled above,
      // re-push of identical path): trail unchanged.
    } else if (isTopLevelRoute(location.pathname)) {
      // Top-level pages are breadcrumb roots: arriving at one resets the trail.
      stack = [];
    } else {
      // Returning to a page already in the trail (back bar, browser back, or
      // any in-page link going "up"): unwind to it instead of pushing, so its
      // back target is the page before it — not the deeper page just left.
      const idx = stack.map((e) => e.to).lastIndexOf(currentPath);
      if (idx !== -1) {
        stack = stack.slice(0, idx);
      } else if (navigationType === 'PUSH') {
        // Back-bar navigations go "up"; the page being left must not become
        // the new page's origin. pageLabelRef still holds the departed page's
        // self-registered name at this point.
        stack = viaBackBar ? stack : [...stack, { to: prev, label: pageLabelRef.current }];
      } else if (navigationType === 'POP') {
        // Browser back to a page not in the trail (e.g. after a reset).
        stack = stack.slice(0, -1);
      }
      // REPLACE leaves the trail untouched: the origin of the replaced page
      // is still the origin of the current one.
    }

    setTrail({ key: location.key, path: currentPath, stack });
  }

  const from = trail.stack.length > 0 ? trail.stack[trail.stack.length - 1] : null;

  const value = useMemo(
    () => ({ override, setOverride, setPageLabel, from }),
    [override, setOverride, setPageLabel, from],
  );

  return <BackBarContext.Provider value={value}>{children}</BackBarContext.Provider>;
}

export function useBackBarContext(): BackBarContextValue {
  const ctx = useContext(BackBarContext);
  if (!ctx) {
    throw new Error('useBackBarContext must be used inside <BackBarProvider>');
  }
  return ctx;
}

/**
 * Pages call this when their parent isn't derivable from the URL alone, or
 * when they want a richer label (e.g. the project name). Pass `null` while
 * data is loading so the registry default keeps working.
 *
 * Override is automatically cleared on unmount.
 */
export function useBackBarOverride(
  override: BackBarOverride | null,
  deps: React.DependencyList,
): void {
  const { setOverride } = useBackBarContext();

  // The caller owns the dep list — typically derived from the entity that
  // produces the label/url. We intentionally don't depend on the override
  // object identity so callers don't have to memoise it.
  useEffect(() => {
    setOverride(override);
    return () => setOverride(null);
  }, deps);
}

/**
 * Pages call this to register their own display name (e.g. the project name).
 * When the user navigates away, the trail records this label so the next
 * page's back bar can immediately say "Back to Transfer Tower #3" instead of
 * a generic route label.
 *
 * Cleared automatically on unmount.
 */
export function useBackBarPageLabel(
  label: string | null,
  deps: React.DependencyList,
): void {
  const { setPageLabel } = useBackBarContext();

  useEffect(() => {
    setPageLabel(label);
    return () => setPageLabel(null);
  }, deps);
}
